import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Music, Trophy, Save, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import Navigation from '@/components/Navigation';
import { useToast } from '@/hooks/use-toast';
import { useStore } from '@/lib/store';

const Profile = () => {
  const { state, updateUser, logout } = useStore();
  const { currentUser } = state;
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    username: currentUser?.username ?? '',
    email: currentUser?.email ?? '',
    favoriteGenre: currentUser?.favorite_genre ?? '',
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  // Update form data when currentUser changes
  useEffect(() => {
    if (!currentUser) {
      return;
    }

    setFormData({
      username: currentUser.username,
      email: currentUser.email,
      favoriteGenre: currentUser.favorite_genre || '',
    });
  }, [currentUser]);

  if (!currentUser) {
    return null;
  }

  const handleSave = () => {
    try {
      updateUser({
        username: formData.username.trim(),
        email: formData.email.trim(),
        favorite_genre: formData.favoriteGenre.trim() || undefined,
      });
      
      toast({
        title: "Profile updated!",
        description: "Your changes have been saved successfully.",
      });
      setIsEditing(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'We could not update your profile.';
      toast({
        title: "Update failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    setFormData({
      username: currentUser.username,
      email: currentUser.email,
      favoriteGenre: currentUser.favorite_genre || '',
    });
    setIsEditing(false);
  };

  const handleLogout = () => {
    logout();
    toast({
      title: "Logged out",
      description: "You've been successfully logged out.",
    });
    navigate('/login');
  };

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calculate achievements dynamically
  const achievements = (() => {
    const userSubmissions = state.submissions.filter(s => s.user_id === currentUser.id);
    const userLeagues = state.leagues.filter(l => 
      l.created_by === currentUser.id || l.member_count > 0 // Simplified check
    );
    
    const hasSubmissions = userSubmissions.length > 0;
    const hasVotes = state.votes.some(v => v.voter_id === currentUser.id);
    
    // Check for round wins
    const roundWins = state.rounds.filter(r => {
      if (r.status !== 'completed') return false;
      const submissions = state.submissions.filter(s => s.round_id === r.id);
      if (submissions.length === 0) return false;
      const winner = submissions.sort((a, b) => b.total_points - a.total_points)[0];
      return winner.user_id === currentUser.id;
    }).length;
    
    const hasWon = roundWins > 0;
    const hasManySubmissions = userSubmissions.length >= 50;
    const hasManyLeagues = userLeagues.length >= 10;

    return [
      { title: 'First Submission', description: 'Submitted your first song', earned: hasSubmissions },
      { title: 'Vote Caster', description: 'Cast your first vote', earned: hasVotes },
      { title: 'Round Winner', description: 'Won your first round', earned: hasWon },
      { title: 'League Champion', description: 'Won an entire league', earned: false }, // Would need league completion logic
      { title: 'Music Curator', description: 'Submitted 50+ songs', earned: hasManySubmissions },
      { title: 'Social Butterfly', description: 'Joined 10+ leagues', earned: hasManyLeagues },
    ];
  })();
  
  // Calculate stats dynamically
  const stats = (() => {
    const userSubmissions = state.submissions.filter(s => s.user_id === currentUser.id);
    const userLeagues = state.leagues.filter(l => 
      l.created_by === currentUser.id || l.member_count > 0
    );
    const roundWins = state.rounds.filter(r => {
      if (r.status !== 'completed') return false;
      const submissions = state.submissions.filter(s => s.round_id === r.id);
      if (submissions.length === 0) return false;
      const winner = submissions.sort((a, b) => b.total_points - a.total_points)[0];
      return winner.user_id === currentUser.id;
    }).length;

    return {
      totalLeagues: userLeagues.length,
      totalWins: roundWins,
      totalSubmissions: userSubmissions.length,
    };
  })();

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background" data-tour="profile">
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-foreground mb-2">Profile Settings</h1>
          <p className="text-muted-foreground">
            Manage your account information and music preferences.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="card-shadow border-2 border-primary/30 bg-gradient-to-br from-purple-100/40 to-blue-100/40 dark:from-purple-950/20 dark:to-blue-950/20 shadow-md">
              <CardHeader className="bg-gradient-to-r from-primary/15 to-blue-500/15 dark:from-primary/20 dark:to-blue-400/20 rounded-t-lg border-b border-primary/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-primary">Personal Information</CardTitle>
                  <Button
                    variant={isEditing ? "outline" : "ghost"}
                    size="sm"
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {isEditing ? 'Cancel' : 'Edit'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Avatar */}
                <div className="flex items-center space-x-4">
                  <Avatar className="w-20 h-20">
                    <AvatarImage src={currentUser.avatar_url} />
                    <AvatarFallback className="bg-music-gradient text-white text-2xl">
                      {currentUser.username.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{currentUser.username}</h3>
                    <p className="text-muted-foreground">Member since 2024</p>
                    {isEditing && (
                      <Button variant="outline" size="sm" className="mt-2">
                        Change Avatar
                      </Button>
                    )}
                  </div>
                </div>

                {/* Form Fields */}
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="username"
                        value={formData.username}
                        onChange={(e) => updateFormData('username', e.target.value)}
                        disabled={!isEditing}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateFormData('email', e.target.value)}
                        disabled={!isEditing}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="favoriteGenre">Favorite Genre</Label>
                    <div className="relative">
                      <Music className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="favoriteGenre"
                        placeholder="e.g., Indie Rock, Jazz, Electronic..."
                        value={formData.favoriteGenre}
                        onChange={(e) => updateFormData('favoriteGenre', e.target.value)}
                        disabled={!isEditing}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                {isEditing && (
                  <div className="flex gap-3 pt-4">
                    <Button onClick={handleSave} className="btn-music">
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button onClick={handleCancel} variant="outline">
                      Cancel
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Achievements */}
            <Card className="card-shadow border-2 border-blue-300/40 bg-gradient-to-br from-blue-100/40 to-indigo-100/40 dark:from-blue-950/20 dark:to-indigo-950/20 shadow-md">
              <CardHeader className="bg-gradient-to-r from-blue-500/15 to-indigo-500/15 dark:from-blue-400/20 dark:to-indigo-400/20 rounded-t-lg border-b border-blue-300/40">
                <CardTitle className="flex items-center text-blue-600 dark:text-blue-400">
                  <Trophy className="h-5 w-5 mr-2" />
                  Achievements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  {achievements.map((achievement, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border ${
                        achievement.earned
                          ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                          : 'bg-muted/50 border-border'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          achievement.earned
                            ? 'bg-green-500 text-white'
                            : 'bg-muted-foreground/20 text-muted-foreground'
                        }`}>
                          <Trophy className="h-4 w-4" />
                        </div>
                        <div>
                          <h4 className={`font-medium ${
                            achievement.earned ? 'text-foreground' : 'text-muted-foreground'
                          }`}>
                            {achievement.title}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {achievement.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats & Actions */}
          <div className="space-y-6">
            <Card className="card-shadow border-2 border-indigo-300/40 bg-gradient-to-br from-indigo-100/30 to-purple-100/30 dark:from-indigo-950/15 dark:to-purple-950/15 shadow-md">
              <CardHeader className="bg-gradient-to-r from-accent/15 via-primary/15 to-indigo-500/15 dark:from-accent/20 dark:via-primary/20 dark:to-indigo-400/20 rounded-t-lg border-b border-indigo-300/40">
                <CardTitle className="text-indigo-600 dark:text-indigo-400">Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-3xl font-bold text-primary">{stats.totalLeagues}</p>
                  <p className="text-sm text-muted-foreground">Active Leagues</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-3xl font-bold text-primary">{stats.totalWins}</p>
                  <p className="text-sm text-muted-foreground">Round Wins</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-3xl font-bold text-primary">{stats.totalSubmissions}</p>
                  <p className="text-sm text-muted-foreground">Songs Submitted</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow border-2 border-secondary/30 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 dark:from-blue-950/15 dark:to-indigo-950/15 shadow-md">
              <CardHeader className="bg-gradient-to-r from-blue-500/15 to-indigo-500/15 dark:from-blue-400/20 dark:to-indigo-400/20 rounded-t-lg border-b border-secondary/30">
                <CardTitle className="text-blue-600 dark:text-blue-400">Account Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full">
                  Change Password
                </Button>
                <Button variant="outline" className="w-full">
                  Download Data
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={handleLogout}
                >
                  Sign Out
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;